/** @format */

import { PlayCircleOutlined, PoweroffOutlined, QrcodeOutlined } from "@ant-design/icons";
import { Button, Popconfirm } from "antd";
import moment from "moment";
import React from "react";
import { THome } from "../../../../redux-modules/declarations/maps/home";
import {
  TGiornoPresenze,
  TTurnoPresenze,
} from "../../../../redux-modules/declarations/maps/presenzeVista";
import { TPresence } from "../../../../redux-modules/declarations/maps/presences";
import { BaseColor } from "../../../../theme/maps/Colors";
import { assenzaDelGiorno, FASCE, soloRiposo, tinta, titoloTurno } from "../../ViewPresenze/format";
import { oreAsse } from "../../ViewPresenze/linea";
import { LineaGiorno } from "../../ViewPresenze/partials/LineaGiorno";
import { durataDiscorsiva, giornoRelativo, minutiTra, orario } from "../format";

interface Props {
  home: THome;
  giorno: TGiornoPresenze | null;
  /** presenza aperta da GET /presences/check (la stessa lettura della Home precedente) */
  aperta: TPresence | null;
  /** "YYYY-MM-DD HH:mm" del browser, aggiornato ogni 30 secondi */
  adesso: string;
  inApp: boolean;
  onTimbra: () => void;
  onChiudiDalWeb: () => void;
}

interface TStato {
  colore: "verde" | "blu" | "rosso" | "grigio";
  riga1: string;
  grande: string | null;
  riga3: string | null;
  vivo: boolean;
  /** c'è un turno da iniziare: la timbratura è l'azione principale */
  ingresso: boolean;
}

/** "DD/MM/YYYY HH:mm:ss" di /presences/check → "YYYY-MM-DD HH:mm" */
const daCheck = (valore: string): string =>
  moment(valore, "DD/MM/YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm");

const testoProssimo = (home: THome): string | null => {
  const p = home.prossimo_turno;
  if (!p) return null;
  return `Prossimo turno ${giornoRelativo(p.data, home.oggi)}, ${orario(p.ora_inizio, p.ora_fine)}`;
};

/** Lo stato della giornata in tre righe: cosa succede, quanto, e il contesto. */
const statoDelGiorno = (
  home: THome,
  giorno: TGiornoPresenze | null,
  aperta: TPresence | null,
  adesso: string,
): TStato => {
  const turni = (giorno?.turni || []).filter(t => t.lavorativo && !t.giorno_prima);

  if (aperta) {
    const entrata = daCheck(aperta.datetime_from);
    const minuti = Math.max(0, minutiTra(entrata, adesso));
    const turno = (giorno?.turni || []).find(t => t.presenze.indexOf(Number(aperta.id)) >= 0);
    let riga3 = aperta.presence_type_name;
    if (turno) {
      const mancano = minutiTra(adesso, turno.fine);
      riga3 += ` · turno ${orario(turno.ora_inizio, turno.ora_fine)}`;
      riga3 +=
        mancano > 0 ? `, fine tra ${durataDiscorsiva(mancano)}` : `, finito alle ${turno.ora_fine}`;
    } else {
      riga3 += " · senza turno";
    }
    const giornoEntrata = entrata.substr(0, 10);
    return {
      colore: "verde",
      riga1:
        giornoEntrata === home.oggi
          ? `In servizio dalle ${entrata.substr(11, 5)}`
          : `In servizio da ${giornoRelativo(giornoEntrata, home.oggi)} alle ${entrata.substr(
              11,
              5,
            )}`,
      grande: durataDiscorsiva(minuti),
      riga3,
      vivo: true,
      ingresso: false,
    };
  }

  if (giorno) {
    const assenza = assenzaDelGiorno(giorno);
    if (assenza) {
      return {
        colore: "grigio",
        riga1: assenza.stato === "accettata" ? "Oggi sei assente" : "Assenza richiesta, in attesa",
        grande: assenza.nome,
        riga3: testoProssimo(home),
        vivo: false,
        ingresso: false,
      };
    }
  }

  const daFare = turni.find(t => t.stato === "da_svolgere" || t.stato === "mancata_timbratura");
  if (daFare && daFare.fine > adesso) {
    const traQuanto = minutiTra(adesso, daFare.inizio);
    const sede = daFare.sede ? ` · ${daFare.sede}` : "";
    if (traQuanto > 0) {
      return {
        colore: "blu",
        riga1: `Il turno inizia alle ${daFare.ora_inizio}`,
        grande: `tra ${durataDiscorsiva(traQuanto)}`,
        riga3: `Turno ${orario(daFare.ora_inizio, daFare.ora_fine)}${sede}`,
        vivo: false,
        ingresso: true,
      };
    }
    return {
      colore: "rosso",
      riga1: `Turno iniziato alle ${daFare.ora_inizio}, non hai timbrato`,
      grande: `da ${durataDiscorsiva(-traQuanto)}`,
      riga3: `Turno ${orario(daFare.ora_inizio, daFare.ora_fine)}${sede}`,
      vivo: false,
      ingresso: true,
    };
  }

  if (giorno && soloRiposo(giorno)) {
    const riposo = giorno.turni.find(t => !t.giorno_prima);
    return {
      colore: "grigio",
      riga1: "Oggi",
      grande: riposo ? titoloTurno(riposo) : "Riposo",
      riga3: testoProssimo(home),
      vivo: false,
      ingresso: false,
    };
  }

  if (turni.length > 0 && giorno) {
    const nonTimbrati = turni.filter(t => t.stato === "mancata_timbratura").length;
    return {
      colore: nonTimbrati > 0 ? "rosso" : "grigio",
      riga1:
        nonTimbrati > 0
          ? nonTimbrati === 1
            ? "Giornata conclusa, un turno non timbrato"
            : `Giornata conclusa, ${nonTimbrati} turni non timbrati`
          : "Giornata conclusa",
      grande: `${durataDiscorsiva(giorno.totali.minuti_lavorati)} lavorate`,
      riga3: testoProssimo(home),
      vivo: false,
      ingresso: false,
    };
  }

  return {
    colore: "grigio",
    riga1: "Oggi non hai turni programmati",
    grande: null,
    riga3: testoProssimo(home),
    vivo: false,
    ingresso: false,
  };
};

/** Fascia della linea: 07–21, oppure 24 ore se qualcosa del giorno ne esce. */
/** Le presenze aperte arrivano fino ad adesso. */
const fasciaDelGiorno = (
  giorno: TGiornoPresenze,
  adesso: string,
): { da: number; a: number; passo: number } => {
  const fuori = (dataOra: string | null): boolean => {
    if (!dataOra) return false;
    if (dataOra.substr(0, 10) !== giorno.data) return true;
    const m = +dataOra.substr(11, 2) * 60 + +dataOra.substr(14, 2);
    return m < FASCE.lavoro.da || m > FASCE.lavoro.a;
  };
  const esce =
    giorno.turni.some((t: TTurnoPresenze) => t.lavorativo && (fuori(t.inizio) || fuori(t.fine))) ||
    giorno.presenze.some(p => fuori(p.entrata) || fuori(p.aperta ? adesso : p.uscita));
  return esce ? FASCE.giorno : FASCE.lavoro;
};

/** Striscia "Oggi": stato della giornata, linea oraria e timbratura. */
export const Oggi = ({
  home,
  giorno,
  aperta,
  adesso,
  inApp,
  onTimbra,
  onChiudiDalWeb,
}: Props): JSX.Element => {
  const stato = statoDelGiorno(home, giorno, aperta, adesso);
  const fascia = giorno ? fasciaDelGiorno(giorno, adesso) : FASCE.lavoro;
  const assenza = giorno ? assenzaDelGiorno(giorno) : null;
  const ingresso = !aperta && stato.ingresso;
  const entrata = aperta ? daCheck(aperta.datetime_from).substr(11, 5) : "";

  let azione: JSX.Element;
  if (aperta && inApp) {
    azione = (
      <Popconfirm
        title={`Chiudere la timbratura iniziata alle ${entrata}?`}
        okText="Chiudi timbratura"
        cancelText="Annulla"
        onConfirm={onChiudiDalWeb}
        placement="bottomRight"
      >
        <Button type="primary" size="large" icon={<PoweroffOutlined />}>
          Chiudi timbratura
        </Button>
      </Popconfirm>
    );
  } else if (aperta) {
    azione = (
      <Button type="primary" size="large" icon={<QrcodeOutlined />} onClick={onTimbra}>
        Mostra il QR per uscire
      </Button>
    );
  } else {
    const testo = inApp ? (ingresso ? "Timbra ingresso" : "Timbra") : "Mostra il QR per timbrare";
    azione = (
      <Button
        type={ingresso ? "primary" : "default"}
        size="large"
        icon={inApp ? <PlayCircleOutlined /> : <QrcodeOutlined />}
        onClick={onTimbra}
      >
        {testo}
      </Button>
    );
  }

  return (
    <section className="oggi" aria-label="Oggi">
      <div className="stato">
        <span className={`riga1 ${stato.colore}`}>
          {stato.vivo && <span className="punto" aria-hidden="true" />}
          {stato.riga1}
        </span>
        {stato.grande && <span className="grande numeri">{stato.grande}</span>}
        {stato.riga3 && <span className="riga3">{stato.riga3}</span>}
      </div>

      <div>
        {giorno && !assenza ? (
          <>
            <LineaGiorno giorno={giorno} fascia={fascia} adesso={adesso} pixel={560} compatta />
            <div className="asse numeri" aria-hidden="true">
              {oreAsse(fascia, fascia.passo * 2).map((h, i, tutte) => (
                <span
                  key={h.etichetta}
                  className={i === 0 ? "prima" : i === tutte.length - 1 ? "ultima" : undefined}
                  style={{ left: `${h.sinistra}%` }}
                >
                  {h.etichetta}
                </span>
              ))}
            </div>
          </>
        ) : assenza ? (
          <span
            className="assenza"
            style={{
              background: tinta(assenza.colore, 0.18),
              borderColor: tinta(assenza.colore, 0.6),
              borderStyle: assenza.stato === "accettata" ? "solid" : "dashed",
            }}
          >
            <span className="pallino" style={{ background: assenza.colore || BaseColor }} />
            {assenza.nome}, tutto il giorno{assenza.stato === "in_attesa" ? " (in attesa)" : ""}
          </span>
        ) : null}
      </div>

      <div className="azioni">
        {azione}
        {!inApp && !aperta && (
          <span className="nota">Il codice si legge con il tablet della sede</span>
        )}
      </div>
    </section>
  );
};
